import { ColumnDef } from '@tanstack/react-table';

import { BillingGroupRun, GUID } from '@ndvr/billing-api';

import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import type { BillingRunTableRow } from './billing-run-table';

export type BillingRunSelectionColumnOptions = {
    selectedRunGUIDs: Set<GUID<BillingGroupRun>>;
    getSelectionBlockReason: (run: BillingGroupRun) => string | undefined;
    onRunsSelectionChange: (runGUIDs: GUID<BillingGroupRun>[], selected: boolean) => void;
};

export const createBillingRunSelectionColumn = ({
    selectedRunGUIDs,
    getSelectionBlockReason,
    onRunsSelectionChange,
}: BillingRunSelectionColumnOptions): ColumnDef<BillingRunTableRow> => ({
    id: 'select',
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
    enableSorting: false,
    header: ({ table }) => {
        const visibleRunGUIDs = table
            .getRowModel()
            .rows.filter((row) => row.original.kind === 'group' && getSelectionBlockReason(row.original.run) == null)
            .map((row) => row.original.run.billingGroupRunGUID);
        const allSelected =
            visibleRunGUIDs.length > 0 && visibleRunGUIDs.every((runGUID) => selectedRunGUIDs.has(runGUID));
        const someSelected = visibleRunGUIDs.some((runGUID) => selectedRunGUIDs.has(runGUID));

        return (
            <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={(checked) => onRunsSelectionChange(visibleRunGUIDs, checked === true)}
                aria-label="Select all billing runs"
            />
        );
    },
    cell: ({ row }) => {
        if (row.original.kind !== 'group') return null;
        const run = row.original.run;
        const disabledReason = getSelectionBlockReason(run);
        if (disabledReason == null) {
            return (
                <Checkbox
                    checked={selectedRunGUIDs.has(run.billingGroupRunGUID)}
                    onCheckedChange={(checked) => onRunsSelectionChange([run.billingGroupRunGUID], checked === true)}
                    aria-label="Select billing run"
                    onClick={(event) => event.stopPropagation()}
                />
            );
        }

        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex" onClick={(event) => event.stopPropagation()}>
                        <Checkbox disabled aria-label="Billing run unavailable for selection" />
                    </span>
                </TooltipTrigger>
                <TooltipContent>{disabledReason}</TooltipContent>
            </Tooltip>
        );
    },
    meta: {
        headerClassName: 'bg-gray-50',
    },
});
